import axios from 'axios';
import type { DataTableSort } from '@bcecloud/portal-ui';
import { authHeader } from '@/lib/events';
import { ACTIVITY_KIND, SALES_WORKSPACE_PATH } from '@/lib/sales-workspace';

/**
 * The sales read layer client (/api/v3/sales-data): accounts, contacts and a
 * deal's context, for the sales workspace pages. Dark like My day: the API
 * answers 403 to anyone off its allow-list, and the pages then act as an
 * unknown address. Shapes mirror apps/api/.../sales-data.views.ts.
 */

// ---- Paths -------------------------------------------------------------------

export const SALES_ACCOUNTS_PATH = `${SALES_WORKSPACE_PATH}/accounts`;
export const SALES_CONTACTS_PATH = `${SALES_WORKSPACE_PATH}/contacts`;
export const salesAccountPath = (id: string): string => `${SALES_ACCOUNTS_PATH}/${encodeURIComponent(id)}`;
export const salesContactPath = (id: string): string => `${SALES_CONTACTS_PATH}/${encodeURIComponent(id)}`;

// ---- Shapes ------------------------------------------------------------------

export interface AccountSummary {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly industry: string;
  readonly billingCity: string;
  readonly billingState: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly lastActivityDate: string | null;
}

export interface AccountListRow extends AccountSummary {
  readonly openDeals: number;
  readonly bookings: number;
  /** Contract total of signed deals. */
  readonly bookedValue: number;
  readonly lastBookedDate: string | null;
}

export interface DealRow {
  readonly id: string;
  readonly name: string;
  readonly accountId: string;
  readonly accountName: string;
  readonly stageName: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly isClosed: boolean;
  readonly booked: boolean;
  readonly createdDate: string;
  readonly signedDate: string | null;
  readonly eventDate: string | null;
  readonly programName: string;
  readonly participants: number | null;
  readonly totalCost: number | null;
}

export interface ContactRow {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly email: string;
  readonly phone: string;
  readonly accountId: string;
  readonly accountName: string;
  readonly ownerName: string;
  readonly lastActivityDate: string | null;
  /** Opted out of email: never send them outreach. */
  readonly emailOptOut: boolean;
  readonly doNotCall: boolean;
}

export const TIMELINE_KIND = { ...ACTIVITY_KIND, meeting: 'meeting' } as const;
export type TimelineKind = (typeof TIMELINE_KIND)[keyof typeof TIMELINE_KIND];

export interface TimelineItem {
  readonly id: string;
  readonly kind: TimelineKind;
  readonly title: string;
  /** ISO instant. */
  readonly at: string;
  readonly opportunityId: string;
  readonly contactId: string;
  /** Who it was with; empty when unknown. */
  readonly contactName: string;
  readonly ownerId: string;
}

export interface AccountDetail {
  readonly account: AccountSummary & {
    readonly phone: string;
    readonly website: string;
    readonly parentId: string;
    readonly parentName: string;
  };
  readonly openDeals: readonly DealRow[];
  readonly pastBookings: readonly DealRow[];
  readonly otherClosedDeals: readonly DealRow[];
  readonly contacts: readonly ContactRow[];
  readonly timeline: readonly TimelineItem[];
}

export interface ContactDeal {
  readonly deal: DealRow;
  readonly role: string;
  readonly isPrimary: boolean;
}

export interface ContactDetail {
  readonly contact: ContactRow;
  readonly deals: readonly ContactDeal[];
  readonly timeline: readonly TimelineItem[];
}

export interface DealContact {
  readonly contact: ContactRow;
  readonly role: string;
  readonly isPrimary: boolean;
}

export interface OpportunityContext {
  readonly deal: DealRow;
  readonly account: AccountSummary | null;
  readonly contacts: readonly DealContact[];
  /** What the account booked before, this deal excluded. */
  readonly pastBookings: readonly DealRow[];
  readonly timeline: readonly TimelineItem[];
}

export interface AccountOwner {
  readonly ownerId: string;
  readonly ownerName: string;
  readonly accounts: number;
}

export interface SalesPage<T> {
  readonly rows: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// ---- Sorting -----------------------------------------------------------------

/** The account list's sortable columns, as the API names them. */
export const ACCOUNT_SORT_FIELDS = {
  name: 'name',
  location: 'location',
  owner: 'owner',
  openDeals: 'openDeals',
  bookings: 'bookings',
  bookedValue: 'bookedValue',
  lastBooked: 'lastBooked',
} as const;
export const ACCOUNT_SORT_KEYS = Object.values(ACCOUNT_SORT_FIELDS);
export const ACCOUNT_DEFAULT_SORT: DataTableSort = { key: ACCOUNT_SORT_FIELDS.name, direction: 'asc' };

/** The contact list's sortable columns; with none, the most recently active come first. */
export const CONTACT_SORT_FIELDS = {
  name: 'name',
  account: 'account',
  title: 'title',
  lastActivity: 'lastActivity',
} as const;
export const CONTACT_SORT_KEYS = Object.values(CONTACT_SORT_FIELDS);
export const CONTACT_DEFAULT_SORT: DataTableSort = { key: CONTACT_SORT_FIELDS.lastActivity, direction: 'desc' };

// ---- Reads -------------------------------------------------------------------

const BASE = '/api/v3/sales-data';

async function getData<T>(path: string, params: Readonly<Record<string, string | number>>, signal?: AbortSignal): Promise<T> {
  const response = await axios.get<{ data: T }>(`${BASE}${path}`, {
    params,
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

function listParams(input: { readonly q: string; readonly page: number; readonly sort: DataTableSort | undefined }): Record<string, string | number> {
  return {
    q: input.q,
    page: input.page,
    ...(input.sort ? { sortField: input.sort.key, sortDirection: input.sort.direction } : {}),
  };
}

export const listSalesAccounts = (
  input: { readonly q: string; readonly ownerId: string; readonly page: number; readonly sort: DataTableSort | undefined },
  signal?: AbortSignal,
): Promise<SalesPage<AccountListRow>> =>
  getData('/accounts', { ...listParams(input), ...(input.ownerId ? { ownerId: input.ownerId } : {}) }, signal);

export const listAccountOwners = (signal?: AbortSignal): Promise<readonly AccountOwner[]> => getData('/account-owners', {}, signal);

export const readSalesAccount = (id: string, signal?: AbortSignal): Promise<AccountDetail> =>
  getData(`/accounts/${encodeURIComponent(id)}`, {}, signal);

export const listSalesContacts = (
  input: { readonly q: string; readonly page: number; readonly sort: DataTableSort | undefined },
  signal?: AbortSignal,
): Promise<SalesPage<ContactRow>> => getData('/contacts', listParams(input), signal);

export const readSalesContact = (id: string, signal?: AbortSignal): Promise<ContactDetail> =>
  getData(`/contacts/${encodeURIComponent(id)}`, {}, signal);

export const readOpportunityContext = (id: string, signal?: AbortSignal): Promise<OpportunityContext> =>
  getData(`/opportunities/${encodeURIComponent(id)}/context`, {}, signal);

// ---- Formatting --------------------------------------------------------------

/** "Boston, MA", or empty. */
export const placeOf = (city: string, state: string): string => [city, state].filter(Boolean).join(', ');

/** How many pages a list has. */
export const pageCount = (page: Pick<SalesPage<unknown>, 'total' | 'pageSize'>): number => Math.max(1, Math.ceil(page.total / page.pageSize));
